import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { getPeople } from '@/store/people/people.api';
import { peopleKeys } from '@/store/people/people.keys';
import type { Person, SeatType } from '@/store/people/people.types';

import {
  cancelSubscription,
  createSubscriptionCheckout,
  createSubscriptionPortal,
  getAllSubscriptions,
  getBilledServiceIds,
  getSubscription,
  getSubscriptionPrices,
  uncancelSubscription,
  updateSubscription,
} from './subscriptions.api';
import { subscriptionKeys } from './subscriptions.keys';
import { SubscriptionTier, seatTypeFor } from './subscriptions.types';
import type {
  CancelSubscription,
  CreateCheckoutSubscription,
  GetAllSubscriptionsParams,
  Subscription,
  SubscriptionMutationParams,
  UpdateSubscription,
} from './subscriptions.types';

/**
 * Folds the per-service reads into one lookup. Module level, so its identity is stable —
 * `useQueries` memoises on the combine function, and an inline one would rebuild the map on
 * every render. Keyed on each row's own `serviceId`, so it closes over nothing.
 *
 * Only the services that answered with a row are in it: a failed read is not "no plan", and
 * neither absence nor a `null` answer can be told from the other here — both read as the free
 * plan, which is what an unanswered service falls back to anyway.
 */
const combineSubscriptions = (results: UseQueryResult<Subscription | null>[]) => ({
  byServiceId: new Map(
    results.flatMap((result) =>
      result.data ? [[result.data.serviceId, result.data] as const] : [],
    ),
  ),
  isPending: results.some((result) => result.isPending),
  error: results.find((result) => result.error)?.error ?? null,
});

/** The named service's own subscription, or `null` when it has none. */
export const useSubscriptionQuery = (serviceId?: string) =>
  useQuery({
    queryKey: subscriptionKeys.detail(serviceId!),
    queryFn: () => getSubscription({ serviceId: serviceId! }),
    enabled: !!serviceId,
  });

/**
 * One subscription per named service, each read on its own.
 *
 * A vessel at a time rather than `/subscriptions/all` in one request, because that endpoint
 * answers a different question: the rows whose `person_id` is the caller — what they are
 * *billed for*. A captain listing a vessel whose owner pays would find no row of theirs for
 * it and read the plan as free, and so would an owner whose captain pays for their boat.
 * `GET /subscriptions` asks about the service instead, which is what the rows state.
 *
 * They share `subscriptionKeys.detail` with `useSubscriptionQuery`, so opening a row costs
 * no request at all — the list has already fetched and cached the very query the detail view
 * reads.
 */
export const useSubscriptionsQuery = (serviceIds: string[]) =>
  useQueries({
    queries: serviceIds.map((serviceId) => ({
      queryKey: subscriptionKeys.detail(serviceId),
      queryFn: () => getSubscription({ serviceId }),
    })),
    combine: combineSubscriptions,
  });

/**
 * Every subscription the caller is *billed for*, across all their services — which is not the
 * same set as the vessels whose plans they manage, so the account list reads each vessel's own
 * row through `useSubscriptionsQuery` instead. This is the endpoint for a question about the
 * bill rather than about a vessel.
 */
export const useAllSubscriptionsQuery = ({
  serviceId,
  ...params
}: Partial<GetAllSubscriptionsParams>) =>
  useQuery({
    queryKey: subscriptionKeys.allList({ serviceId, ...params }),
    queryFn: () => getAllSubscriptions({ serviceId: serviceId!, ...params }),
    enabled: !!serviceId,
  });

/**
 * Which of the caller's vessels are on *their* card.
 *
 * The vessel's own row says what the plan is and never who pays for it, so this is what a
 * screen asks before offering to change a plan: an owner looking at a plan their captain
 * bought must be sent to a fresh checkout — which the server reads as a takeover — rather
 * than to `PATCH`, which would quietly re-price somebody else's subscription.
 *
 * `serviceId` only satisfies the `X-Service-Id` middleware; the answer is the caller's
 * either way, which is why it is deliberately absent from the key. The first vessel to ask
 * fills the cache for all of them.
 */
export const useBilledServiceIdsQuery = (serviceId?: string) =>
  useQuery({
    queryKey: subscriptionKeys.billed,
    queryFn: () => getBilledServiceIds({ serviceId: serviceId! }),
    enabled: !!serviceId,
  });

/**
 * Stripe's price for each lookup key. Held for the whole session: the catalogue moves when
 * the products are repriced, which is a deploy, not something this page can cause.
 *
 * It backs both the bill on the detail view and every amount in the plan form, where the
 * radios re-price on each keystroke against the same cached keys.
 */
export const useSubscriptionPricesQuery = (serviceId: string | undefined, lookupKeys: string[]) =>
  useQuery({
    queryKey: subscriptionKeys.prices(serviceId!, lookupKeys),
    queryFn: () => getSubscriptionPrices({ serviceId: serviceId!, lookupKeys }),
    enabled: !!serviceId && lookupKeys.length > 0,
    staleTime: Infinity,
  });

const useInvalidateSubscriptions = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
};

/**
 * What a plan change reaches: the subscription row, and people — the webhook behind the
 * change rewrites the `seat_type` on the service's roles whenever the tier changes or a
 * pool shrinks, so the seat rows are left as stale as the plan was. The mirror of
 * `invalidateSeats` on the people side, which invalidates both for the same reason.
 */
const useInvalidatePlan = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all }),
      queryClient.invalidateQueries({ queryKey: peopleKeys.all }),
    ]);
};

/**
 * Starts a paid subscription for the given service and resolves to the Stripe checkout
 * URL — redirect the user there. The row already exists as `incomplete` at that point;
 * it flips to active only once Stripe's webhook lands, so a refetch right after the
 * user returns may still read incomplete.
 */
export const useSubscriptionCheckoutMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: (params: { serviceId: string; input: CreateCheckoutSubscription }) =>
      createSubscriptionCheckout(params),
    onSuccess: invalidate,
  });
};

/**
 * Whether the stored row already reflects a change that was sent. Only the fields the
 * request carried are compared — an omitted one was meant to keep its current value.
 */
const isPlanApplied = (row: Subscription | null, input: UpdateSubscription): row is Subscription =>
  !!row &&
  (input.tier === undefined || row.tier === input.tier) &&
  (input.interval === undefined || row.interval === input.interval) &&
  (input.seats === undefined || row.seats === input.seats) &&
  (input.crewSeats === undefined || row.crewSeats === input.crewSeats);

/** How many of the service's people hold this seat. */
const seatsTaken = (people: Person[], serviceId: string, seatType: SeatType | null): number =>
  seatType === null
    ? 0
    : people.filter((person) =>
        person.services.some(
          (service) => service.serviceId === serviceId && service.seatType === seatType,
        ),
      ).length;

/**
 * Whether the service's people already sit as the stored plan says they should.
 *
 * `reconcileSeatsForPlan` (People's `internal/app/payments/helpers.go`) runs *after* the
 * subscription row is committed, and behind two calls of its own — the invoice fetch and
 * the wallet transaction — so a row that reads as changed is no proof that the seats have
 * been rewritten yet. It empties every seat of the outgoing tier, empties a pool that now
 * holds more people than it pays for, and re-seats the owner last. Both halves show here:
 *
 *  - somebody holds the tier's management seat → the closing re-seat has landed;
 *  - neither pool holds more people than the plan pays for → the emptying has.
 *
 * Refetching before that is what left the seat rows empty until a reload: the cached people
 * still carried the *old* tier's `seat_type`, which matches no pool of the new tier.
 *
 * Asked outside the cache, because it is the invalidation after this wait that refills the
 * rows the view reads. A probe that fails answers `true`: the plan itself is applied, and a
 * question that cannot be asked will not be answered by asking it again.
 */
const areSeatsApplied = async (serviceId: string, row: Subscription): Promise<boolean> => {
  const management = seatTypeFor(row.tier, 'management');

  // Harbor reconciles nothing: its seat is the free default every role already holds, so
  // counting it would prove nothing. `PATCH` cannot ask for harbor anyway.
  if (!management || row.tier === SubscriptionTier.HARBOR) return true;

  try {
    const people = await getPeople({ serviceId });
    const seated = seatsTaken(people, serviceId, management);

    return (
      seated > 0 &&
      seated <= row.seats &&
      seatsTaken(people, serviceId, seatTypeFor(row.tier, 'crew')) <= row.crewSeats
    );
  } catch {
    return true;
  }
};

/** How long to wait for the webhook before showing whatever the row says. */
const PLAN_SYNC_ATTEMPTS = 5;
const PLAN_SYNC_DELAY_MS = 1200;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `PATCH /subscriptions/{id}` only reaches Stripe. It answers 204 without touching the
 * People database: seats, tier and interval are written when the resulting
 * `customer.subscription.updated` webhook is handled — `syncSubscriptionRecord` →
 * `UpdateSubscriptionPlanByStripeID(planFromStripeItems(...))` in the People service's
 * `internal/app/payments/service.go`.
 *
 * So refetching the moment the 204 lands reads the plan as it was before the change — and,
 * a beat later, the seats as they were too. This refetches until both the row and the seats
 * agree with what was sent, and gives up with `false` rather than blocking forever if the
 * webhook never arrives. That is also what a declined card looks like: the patch is sent
 * `pending_if_incomplete`, so Stripe holds the change and nothing is written.
 */
const waitForPlan = async (
  queryClient: QueryClient,
  serviceId: string,
  input: UpdateSubscription,
): Promise<boolean> => {
  for (let attempt = 0; attempt < PLAN_SYNC_ATTEMPTS; attempt++) {
    await wait(PLAN_SYNC_DELAY_MS);

    const row = await queryClient.fetchQuery({
      queryKey: subscriptionKeys.detail(serviceId),
      queryFn: () => getSubscription({ serviceId }),
      staleTime: 0,
    });

    if (isPlanApplied(row, input) && (await areSeatsApplied(serviceId, row))) return true;
  }
  return false;
};

/**
 * Changes tier / interval / seats on a paid plan. `url` is `null` once the change is
 * applied, or a Stripe URL if the server turned it into a checkout — which it only does
 * for a `harbor` row, and those go to the checkout endpoint directly. The caller handles
 * both anyway.
 *
 * The mutation stays pending until the change is readable — the plan and the seats the same
 * webhook rewrites — so the form's "Saving…" covers that round trip instead of closing onto
 * the old counts, or onto seats that read as empty because their type moved with the tier.
 * `applied` says whether it ever became readable: `false` for a checkout, a declined card, or
 * a webhook that is late.
 */
export const useUpdateSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidatePlan();
  return useMutation({
    mutationFn: async (params: SubscriptionMutationParams & { input: UpdateSubscription }) => {
      const url = await updateSubscription(params);
      // A URL means the change became a checkout instead — the redirect ends this page,
      // and the row is only written once that checkout completes.
      const applied = !url && (await waitForPlan(queryClient, params.serviceId, params.input));
      return { url, applied };
    },
    // `invalidate` refetches people too: the webhook reseats them onto the new plan.
    onSuccess: () => invalidate(),
  });
};

/** Schedules cancellation at period end; access continues until `cancelAt`. */
export const useCancelSubscriptionMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: (params: SubscriptionMutationParams & { input?: CancelSubscription }) =>
      cancelSubscription(params),
    onSuccess: invalidate,
  });
};

export const useUncancelSubscriptionMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: (params: SubscriptionMutationParams) => uncancelSubscription(params),
    onSuccess: invalidate,
  });
};

/** Stripe billing-portal URL — payment methods, invoices, receipts. */
export const useSubscriptionPortalMutation = () =>
  useMutation({
    mutationFn: (params: { serviceId: string }) => createSubscriptionPortal(params),
  });
